import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { ProfileSheet } from "@/components/crew/ProfileSheet";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PUBLIC_LINKS = [
  { to: "/about", label: "About", testId: "nav-about" },
  { to: "/crew-dos-and-donts", label: <>Do's &amp; Dont's</>, testId: "nav-crew-dos" },
  { to: "/register", label: "Apply", testId: "nav-apply" },
  { to: "/crew/login", label: "Crew Access", testId: "nav-crew-access" },
];

const Navigation = ({ userType = "public", userName = "" }) => {
  const navigate = useNavigate();
  const hasAdmin = typeof window !== "undefined" && !!localStorage.getItem("adminToken");

  const goCrew = async () => {
    const crewToken = localStorage.getItem("crewToken");
    if (crewToken) { navigate("/crew/dashboard"); return; }
    const adminToken = localStorage.getItem("adminToken");
    if (adminToken) {
      try {
        const { data } = await axios.post(`${API}/admin/switch-to-crew`, {}, { headers: { Authorization: `Bearer ${adminToken}` } });
        localStorage.setItem("crewToken", data.access_token);
        localStorage.setItem("crewData", JSON.stringify(data.user_data));
        navigate("/crew/dashboard");
      } catch (error) {
        toast.error("Could not open crew view");
      }
    } else {
      navigate("/crew/login");
    }
  };

  const handleLogout = async () => {
    if (userType === "admin") {
      try {
        const token = localStorage.getItem('adminToken');
        if (token) {
          await axios.post(`${API}/admin/logout`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
      } catch (error) {
        console.error("Logout API error:", error);
      }
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminData');
      toast.success("Logged out successfully");
      navigate('/');
    } else if (userType === "crew") {
      try {
        const token = localStorage.getItem('crewToken');
        if (token) {
          await axios.post(`${API}/crew/logout`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
      } catch (error) {
        console.error("Logout API error:", error);
      }
      localStorage.removeItem('crewToken');
      localStorage.removeItem('crewData');
      toast.success("Logged out successfully");
      navigate('/');
    }
  };

  return (
    <nav className="bg-crew-zinc border-b border-zinc-800 sticky top-0 z-50 backdrop-blur-sm bg-opacity-95">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/"
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            data-testid="logo"
          >
            <img 
              src="/verso-logo.png" 
              alt="VERSO Studios" 
              className="h-10 sm:h-16 w-auto"
            />
            <span className="font-black text-crew-yellow text-xl tracking-tighter hidden md:block">
              CREW CALL
            </span>
          </Link>

          {userType === "public" && (
            <div className="flex flex-1 items-center justify-between gap-2 ml-1 sm:ml-6 min-w-0">
              <Link to="/admin/login" data-testid="nav-admin-login">
                <Button variant="ghost" className="text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 text-xs sm:text-base font-semibold px-1.5 sm:px-4">
                  Admin
                </Button>
              </Link>
              <div className="flex items-center gap-0 sm:gap-2">
                {PUBLIC_LINKS.map(({ to, label, testId }) => (
                  <Link key={to} to={to} data-testid={testId}>
                    <Button variant="ghost" className="text-zinc-100 hover:text-crew-yellow hover:bg-zinc-800 text-xs sm:text-base font-semibold px-1.5 sm:px-4 h-auto py-2 whitespace-normal leading-tight text-center">
                      {label}
                    </Button>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Navigation Links */}
          <div className="flex items-center gap-0 sm:gap-3">


            {userType === "admin" && (
              <>
                <Button onClick={() => navigate(-1)} variant="ghost" className="text-zinc-200 hover:text-crew-yellow hover:bg-zinc-800 text-sm sm:text-base font-semibold px-2 sm:px-4" data-testid="nav-back">
                  Back
                </Button>
                <Link to="/" data-testid="nav-home">
                  <Button variant="ghost" className="text-zinc-200 hover:text-crew-yellow hover:bg-zinc-800 text-sm sm:text-base font-semibold px-2 sm:px-4">
                    Home
                  </Button>
                </Link>
                <Button onClick={() => navigate('/admin/dashboard')} variant="ghost" className="text-crew-yellow hover:text-crew-yellow hover:bg-zinc-800 text-sm sm:text-base font-semibold px-2 sm:px-4" data-testid="nav-admin">
                  Admin
                </Button>
                <Button onClick={goCrew} variant="ghost" className="text-zinc-200 hover:text-crew-yellow hover:bg-zinc-800 text-sm sm:text-base font-semibold px-2 sm:px-4" data-testid="nav-crew">
                  Crew
                </Button>
                <Button onClick={handleLogout} variant="ghost" className="text-zinc-200 hover:text-crew-yellow hover:bg-zinc-800 text-sm sm:text-base font-semibold px-2 sm:px-4" data-testid="nav-logout">
                  Logout
                </Button>
              </>
            )}

            {userType === "crew" && (
              <>
                <Button onClick={() => navigate(-1)} variant="ghost" className="text-zinc-200 hover:text-crew-yellow hover:bg-zinc-800 text-sm sm:text-base font-semibold px-2 sm:px-4" data-testid="nav-back">
                  Back
                </Button>
                <Link to="/" data-testid="nav-home">
                  <Button variant="ghost" className="text-zinc-200 hover:text-crew-yellow hover:bg-zinc-800 text-sm sm:text-base font-semibold px-2 sm:px-4">
                    Home
                  </Button>
                </Link>
                {hasAdmin && (
                  <Button onClick={() => navigate('/admin/dashboard')} variant="ghost" className="text-zinc-200 hover:text-crew-yellow hover:bg-zinc-800 text-sm sm:text-base font-semibold px-2 sm:px-4" data-testid="nav-admin">
                    Admin
                  </Button>
                )}
                <Button onClick={() => navigate('/crew/dashboard')} variant="ghost" className="text-crew-yellow hover:text-crew-yellow hover:bg-zinc-800 text-sm sm:text-base font-semibold px-2 sm:px-4" data-testid="nav-crew">
                  Crew
                </Button>
                <ProfileSheet />
                <Button onClick={handleLogout} variant="ghost" className="text-zinc-200 hover:text-crew-yellow hover:bg-zinc-800 text-sm sm:text-base font-semibold px-2 sm:px-4" data-testid="nav-logout">
                  Logout
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
